/**
 * iOS only raises the on-screen keyboard when `focus()` runs synchronously
 * inside a user gesture (the tap/keypress). Our new-note flow focuses the editor
 * only after an async create + Milkdown init — far too late — so the keyboard
 * never appears.
 *
 * Workaround: during the tap, synchronously focus a hidden text input to pop the
 * keyboard immediately. The real editor then takes focus once it mounts; iOS
 * keeps the keyboard open across the hand-off as long as nothing blurs to a
 * non-editable element in between (see Modal's `autoFocusContent`).
 */

let primer: HTMLInputElement | null = null;

function ensurePrimer(): HTMLInputElement {
  if (primer) return primer;
  const el = document.createElement("input");
  el.type = "text";
  el.setAttribute("aria-hidden", "true");
  el.tabIndex = -1;
  Object.assign(el.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width: "1px",
    height: "1px",
    opacity: "0",
    border: "0",
    padding: "0",
    margin: "0",
    // ≥16px so iOS doesn't trigger its focus-zoom on the (invisible) field.
    fontSize: "16px",
    zIndex: "-1",
  });
  document.body.appendChild(el);
  primer = el;
  return el;
}

/** Pop the keyboard now. MUST be called synchronously inside a user gesture. */
export function primeKeyboard(): void {
  try {
    ensurePrimer().focus({ preventScroll: true });
  } catch {
    /* no-op — best effort */
  }
}

/** Dismiss the primed keyboard (e.g. if the action that primed it failed). */
export function cancelPrimedKeyboard(): void {
  if (primer && document.activeElement === primer) primer.blur();
}

/** True if `el` is the hidden primer input — used so focus restoration never
 * lands back on it (which would silently re-open the keyboard). */
export function isPrimer(el: Element | null): boolean {
  return !!primer && el === primer;
}
